import React from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import { Card } from "@UI/Components/Card/Card";
import { Text } from "@UI/Components/Text/Text";
import { Flex } from "@UI/Components/Flex/Flex";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Divider } from "@UI/Components/Divider/Divider";
import { Link } from "@UI/Components/Link/Link";
import { Lock } from "@UI/Components/Icons/Lock";
import { Gift } from "@UI/Components/Icons/Gift";
import { Rocket } from "@UI/Components/Icons/Rocket";
import { Medal } from "@UI/Components/Icons/Medal";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { MagnifyingGlassChart } from "@UI/Components/Icons/MagnifyingGlassChart";
import {
  TAdvantageLevel,
  TNewbieBoostStatus,
} from "@modules/ServiceProviders/services/serviceProviders";
import { ID } from "@types";
import messages from "./messages";

interface IProps {
  advantageLevel: TAdvantageLevel;
  pro: boolean;
  nextEvalDate: string;
  newbieBoostStatus?: TNewbieBoostStatus | null;
  serviceProviderId?: ID;
}

const bold = (...chunks: React.ReactNode[]) => (
  <Text as="span" fontWeight="700">
    {chunks}
  </Text>
);

const bonus = (...chunks: React.ReactNode[]) => (
  <Text as="span" fontWeight="700" color="#FF478F">
    {chunks}
  </Text>
);

const bonusValues = { bold, bonus };

// ── Advantage icon (locked or gradient; sized for mobile row / desktop column) ──

type CardIcon = { variant: "locked" } | { variant: "gradient"; icon: React.ElementType };

const AdvantageIcon = ({ icon, size }: { icon: CardIcon; size: "sm" | "lg" }) => {
  const boxSize = size === "sm" ? "32px" : "64px";
  const iconSize = size === "sm" ? 4 : 7;

  if (icon.variant === "locked") {
    return (
      <Flex w={boxSize} h={boxSize} bg="gray.200" rounded="full" align="center" justify="center" flexShrink={0}>
        <Icon as={Lock} boxSize={iconSize} />
      </Flex>
    );
  }

  return (
    <Flex
      w={boxSize}
      h={boxSize}
      bgGradient="linear(to-r, #FF478F, #FF9800)"
      rounded="full"
      align="center"
      justify="center"
      flexShrink={0}
    >
      <Icon as={icon.icon} color="white" boxSize={iconSize} />
    </Flex>
  );
};

// ── Shared subcomponents ──────────────────────────────────────────────────────

const MobileNextEvalPill = ({ date }: { date: string }) => (
  <Flex bg="sand.200" alignItems="center" px={3} py={1} borderRadius="100px" alignSelf="center">
    <Text color="#666" as="span" fontWeight={400} fontSize="sm">
      <FormattedMessage
        {...messages.nextEvaluation}
        values={{
          magnifyingGlass: () => (
            <Icon as={MagnifyingGlassChart} w={4} h={4} mr={1} color="#666" />
          ),
          nextComputedDate: () => <Text as="span" ml={1}>{date}</Text>,
        }}
      />
    </Text>
  </Flex>
);

const Title = () => (
  <Text fontFamily="heading" fontSize="2xl" color="gray.800">
    <FormattedMessage {...messages.currentAdvantagesTitle} />
  </Text>
);

const NewbieBoostBanner = ({ href }: { href: string }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" w="full" _hover={{ textDecoration: "none" }}>
    <Flex
      bgGradient="linear(to-r, #FF478F, #FF9800)"
      borderRadius="12px"
      align="center"
      justify="space-between"
      gap={3}
      px={4}
      py={3}
      cursor="pointer"
    >
      <Flex align="center" gap={3}>
        <Icon as={Medal} color="white" boxSize={5} />
        <Text color="white" fontSize="md" textAlign="left">
          <FormattedMessage {...messages.newbieBoostBanner} values={{ bold }} />
        </Text>
      </Flex>
      <Icon as={ArrowRight} color="white" boxSize={4} flexShrink={0} />
    </Flex>
  </Link>
);

// ── Card props (desktop only) ─────────────────────────────────────────────────

const cardProps = {
  padding: 4,
  w: "375px",
  maxW: "full",
  textAlign: "center" as const,
  sx: { textWrap: "balance" },
};

// ── Per-state content ─────────────────────────────────────────────────────────

interface CardContent {
  icon: CardIcon;
  topDescription?: React.ReactNode;
  heading?: React.ReactNode;
  subtext: React.ReactNode;
  subtextColor?: string;
  extraText?: React.ReactNode;
  showBanner?: boolean;
}

const getCardContent = ({
  pro,
  newbieBoostStatus,
  advantageLevel,
}: Pick<IProps, "pro" | "newbieBoostStatus" | "advantageLevel">): CardContent => {
  if (pro) {
    return {
      icon: { variant: "gradient", icon: Rocket },
      topDescription: <FormattedMessage {...messages.proDescription} />,
      subtext: <FormattedMessage {...messages.cardBonus2hSooner} values={bonusValues} />,
      extraText: <FormattedMessage {...messages.proProgress} />,
    };
  }

  if (newbieBoostStatus === "active") {
    return {
      icon: { variant: "gradient", icon: Rocket },
      topDescription: <FormattedMessage {...messages.newbieBoostActiveDescription} values={{ bold }} />,
      subtext: <FormattedMessage {...messages.cardBonus2hSooner} values={bonusValues} />,
    };
  }

  if (advantageLevel === "standard") {
    return {
      icon: { variant: "locked" },
      heading: <FormattedMessage {...messages.noAdvantagesYet} />,
      subtext: <FormattedMessage {...messages.noAdvantagesDescription} />,
      subtextColor: "gray.500",
      showBanner: true,
    };
  }

  if (advantageLevel === "great") {
    return {
      icon: { variant: "gradient", icon: Rocket },
      heading: <FormattedMessage {...messages.almostThere} />,
      subtext: <FormattedMessage {...messages.cardBonus2hSooner} values={bonusValues} />,
      showBanner: true,
    };
  }

  // good
  return {
    icon: { variant: "gradient", icon: Gift },
    heading: <FormattedMessage {...messages.onTheRightTrack} />,
    subtext: <FormattedMessage {...messages.cardBonus1hSooner} values={bonusValues} />,
    showBanner: true,
  };
};

// ── Mobile / Desktop layouts ──────────────────────────────────────────────────

const MobileCard = ({
  content,
  nextEvalDate,
  newbieBoostHref,
}: {
  content: CardContent;
  nextEvalDate: string;
  newbieBoostHref?: string;
}) => (
  <Box display={{ base: "block", md: "none" }} sx={{ textWrap: "balance" }}>
    <Divider borderColor="gray.200" mb={4} />
    <Flex direction="column" gap={3}>
      <Title />
      {content.topDescription && (
        <Text color="gray.600" fontSize="sm" textAlign="center">
          {content.topDescription}
        </Text>
      )}
      <Flex direction="column" align="center" gap={2}>
        <AdvantageIcon icon={content.icon} size="sm" />
        {content.heading ? (
          <Flex direction="column" align="center" gap={1}>
            <Text fontWeight="700" color="gray.800" textAlign="center">
              {content.heading}
            </Text>
            <Text fontSize="sm" color={content.subtextColor} textAlign="center">
              {content.subtext}
            </Text>
          </Flex>
        ) : (
          <Text fontSize="sm" textAlign="center">
            {content.subtext}
          </Text>
        )}
      </Flex>
      {content.extraText && (
        <Text fontSize="sm" color="gray.500">
          {content.extraText}
        </Text>
      )}
      {content.showBanner && newbieBoostHref && <NewbieBoostBanner href={newbieBoostHref} />}
      <MobileNextEvalPill date={nextEvalDate} />
    </Flex>
  </Box>
);

const DesktopCard = ({
  content,
  newbieBoostHref,
}: {
  content: CardContent;
  newbieBoostHref?: string;
}) => (
  <Card display={{ base: "none", md: "flex" }} {...cardProps}>
    <Flex direction="column" align="center" gap={4}>
      <Title />
      {content.topDescription && (
        <Text color="gray.600" fontSize="sm">
          {content.topDescription}
        </Text>
      )}
      <Flex direction="column" align="center" gap={2}>
        <AdvantageIcon icon={content.icon} size="lg" />
        {content.heading && (
          <Text fontWeight="700" color="gray.800">
            {content.heading}
          </Text>
        )}
        <Text fontSize="sm" color={content.subtextColor}>
          {content.subtext}
        </Text>
        {content.extraText && (
          <Text fontSize="sm" color="gray.500">
            {content.extraText}
          </Text>
        )}
      </Flex>
      {content.showBanner && newbieBoostHref && <NewbieBoostBanner href={newbieBoostHref} />}
    </Flex>
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────────

export const CurrentAdvantagesCard = ({
  advantageLevel,
  pro,
  nextEvalDate,
  newbieBoostStatus,
  serviceProviderId,
}: IProps) => {
  const { formatMessage } = useI18n();
  const newbieBoostHref =
    newbieBoostStatus === "applicable"
      ? `${formatMessage(messages.newbieBoostFormUrl)}#listworker_id=${serviceProviderId}`
      : undefined;

  const content = getCardContent({ pro, newbieBoostStatus, advantageLevel });

  return (
    <>
      <MobileCard content={content} nextEvalDate={nextEvalDate} newbieBoostHref={newbieBoostHref} />
      <DesktopCard content={content} newbieBoostHref={newbieBoostHref} />
    </>
  );
};
